import { useState } from 'react';
import { X, CheckCircle, XCircle, Calendar, Paperclip } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { toast } from 'sonner';
import { ReasonCodeSelect, getEffectiveReason, type WorkflowModalProps } from './AcknowledgmentSharedUtils';
import { useFormatters } from '../../contexts/PreferencesContext';
import FileDropZone from '../common/FileDropZone';

// Accept Date Modal
export function AcceptDateModal({
  isOpen,
  onClose,
  lineId,
  lineNumber,
  partNumber,
  orderNumber,
  currentDeliveryDate,
  proposedDate,
  onSubmit,
}: WorkflowModalProps) {
  const { formatDate } = useFormatters();
  const [comments, setComments] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await onSubmit({
        lineId,
        action: 'accept',
        comments,
        files: files.length > 0 ? files : undefined,
      });
      setComments('');
      setFiles([]);
      onClose();
    } catch (error) {
      console.error('Failed to accept date:', error);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <Card className="w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-600" />
            {/* CR-4729: Updated wording */}
            <h3 className="text-lg font-semibold">Accept Promise Date</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-muted rounded-full transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <CardContent className="p-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* CR-4729: PO Number above Line, "Promise Date" wording */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
              {orderNumber && (
                <div className="flex justify-between text-sm">
                  <span className="font-medium">PO Number:</span>
                  <span className="font-medium">{orderNumber}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="font-medium">Line:</span>
                <span>{lineNumber}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="font-medium">Part:</span>
                <span className="font-mono">{partNumber}</span>
              </div>
              {/* CR-4729 rework: When a proposed date exists, render
                  "From <old> to <new>" so the user clearly sees the change
                  they're confirming. Falls back to the single-date display
                  for the rare path with no proposed value. */}
              <div className="flex justify-between text-sm">
                <span className="font-medium">Promise Date:</span>
                <span className="font-medium text-green-700">
                  {proposedDate ? (
                    <>
                      From {formatDate(currentDeliveryDate)}
                      {' to '}
                      {formatDate(proposedDate)}
                    </>
                  ) : (
                    formatDate(currentDeliveryDate)
                  )}
                </span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Comments (Optional)
              </label>
              <textarea
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                rows={3}
                placeholder="Add any comments regarding this acceptance..."
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            {/* CR-4723: File attachment support */}
            <div>
              <label className="block text-sm font-medium mb-2">
                <Paperclip className="h-3.5 w-3.5 inline mr-1" />
                Attachments (Optional)
              </label>
              {/* Customer review 11 Sep (drag and drop everywhere). */}
              <FileDropZone
                multiple
                size="compact"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.png,.jpg,.jpeg"
                hint="PDF, Word, Excel, CSV or images"
                onFiles={setFiles}
                data-testid="ack-accept-drop-zone"
              />
              {files.length > 0 && (
                <p className="text-xs text-muted-foreground mt-1">{files.length} file(s) selected</p>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="px-4 py-2 border rounded-lg hover:bg-muted disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
              >
                <CheckCircle className="h-4 w-4" />
                {submitting ? 'Accepting...' : 'Accept Date'}
              </button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

// Decline Date Modal - uses API reason codes
export function DeclineDateModal({
  isOpen,
  onClose,
  lineId,
  lineNumber,
  partNumber,
  orderNumber,
  currentDeliveryDate,
  proposedDate,
  onSubmit,
}: WorkflowModalProps) {
  const { formatDate } = useFormatters();
  const [reasonSelect, setReasonSelect] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [comments, setComments] = useState('');
  const [declineFiles, setDeclineFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const effectiveReason = getEffectiveReason(reasonSelect, customReason);
    if (!effectiveReason) {
      toast.error('Please provide a reason for declining');
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({
        lineId,
        action: 'decline',
        reason: effectiveReason,
        comments,
        files: declineFiles.length > 0 ? declineFiles : undefined,
      });
      setReasonSelect('');
      setCustomReason('');
      setDeclineFiles([]);
      setComments('');
      onClose();
    } catch (error) {
      console.error('Failed to decline date:', error);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <Card className="w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <XCircle className="h-5 w-5 text-red-600" />
            {/* CR-4729: Updated wording */}
            <h3 className="text-lg font-semibold">Decline Promise Date</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-muted rounded-full transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <CardContent className="p-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* CR-4729: PO Number above Line, "Promise Date" wording */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-2">
              {orderNumber && (
                <div className="flex justify-between text-sm">
                  <span className="font-medium">PO Number:</span>
                  <span className="font-medium">{orderNumber}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="font-medium">Line:</span>
                <span>{lineNumber}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="font-medium">Part:</span>
                <span className="font-mono">{partNumber}</span>
              </div>
              {/* CR-4729 rework: Same from-to wording as Accept modal so
                  the user clearly sees the proposal they're declining. */}
              <div className="flex justify-between text-sm">
                <span className="font-medium">Promise Date:</span>
                <span className="font-medium text-red-700">
                  {proposedDate ? (
                    <>
                      From {formatDate(currentDeliveryDate)}
                      {' to '}
                      {formatDate(proposedDate)}
                    </>
                  ) : (
                    formatDate(currentDeliveryDate)
                  )}
                </span>
              </div>
            </div>

            <ReasonCodeSelect
              value={reasonSelect}
              onChange={setReasonSelect}
              customValue={customReason}
              onCustomChange={setCustomReason}
              required
              label="Reason for Decline"
            />

            <div>
              <label className="block text-sm font-medium mb-2">
                Additional Comments
              </label>
              <textarea
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                rows={3}
                placeholder="Provide additional details..."
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            {/* CR-4723: File attachment support */}
            <div>
              <label className="block text-sm font-medium mb-2">
                <Paperclip className="h-3.5 w-3.5 inline mr-1" />
                Attachments (Optional)
              </label>
              {/* Customer review 11 Sep (drag and drop everywhere). */}
              <FileDropZone
                multiple
                size="compact"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.png,.jpg,.jpeg"
                hint="PDF, Word, Excel, CSV or images"
                onFiles={setDeclineFiles}
                data-testid="ack-decline-drop-zone"
              />
              {declineFiles.length > 0 && (
                <p className="text-xs text-muted-foreground mt-1">{declineFiles.length} file(s) selected</p>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="px-4 py-2 border rounded-lg hover:bg-muted disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
              >
                <XCircle className="h-4 w-4" />
                {submitting ? 'Declining...' : 'Decline Date'}
              </button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

// Propose Alternative Date Modal
export function ProposeAlternateDateModal({
  isOpen,
  onClose,
  lineId,
  lineNumber,
  partNumber,
  currentDeliveryDate,
  onSubmit,
}: WorkflowModalProps) {
  const { formatDate } = useFormatters();
  const [proposedDate, setProposedDate] = useState('');
  const [reasonSelect, setReasonSelect] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [comments, setComments] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!proposedDate) {
      toast.error('Please select a proposed delivery date');
      return;
    }
    const effectiveReason = getEffectiveReason(reasonSelect, customReason);
    if (!effectiveReason) {
      toast.error('Please provide a reason for proposing a new date');
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({
        lineId,
        action: 'propose',
        proposedDate,
        reason: effectiveReason,
        comments,
      });
      setProposedDate('');
      setReasonSelect('');
      setCustomReason('');
      setComments('');
      onClose();
    } catch (error) {
      console.error('Failed to propose date:', error);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <Card className="w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-orange-600" />
            <h3 className="text-lg font-semibold">Propose Alternative Date</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-muted rounded-full transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <CardContent className="p-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="font-medium">Line:</span>
                <span>{lineNumber}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="font-medium">Part:</span>
                <span className="font-mono">{partNumber}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="font-medium">Current Date:</span>
                <span className="text-orange-700">
                  {formatDate(currentDeliveryDate)}
                </span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Proposed Promise Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={proposedDate}
                onChange={(e) => setProposedDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                required
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <ReasonCodeSelect
              value={reasonSelect}
              onChange={setReasonSelect}
              customValue={customReason}
              onCustomChange={setCustomReason}
              required
              label="Reason for Change"
            />

            <div>
              <label className="block text-sm font-medium mb-2">
                Additional Comments
              </label>
              <textarea
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                rows={3}
                placeholder="Provide additional details about the proposed change..."
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="px-4 py-2 border rounded-lg hover:bg-muted disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 flex items-center gap-2"
              >
                <Calendar className="h-4 w-4" />
                {submitting ? 'Proposing...' : 'Propose Date'}
              </button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

// Re-export ProposeChangeModal from its own module (file size split)
export { ProposeChangeModal } from './ProposeChangeModal';

// Re-export ReasonCodeSelect for use in bulk modals
export { ReasonCodeSelect, getEffectiveReason } from './AcknowledgmentSharedUtils';
